import { graphql } from 'gatsby'
import React from 'react'
import {
  MenuCarousel,
  MenuCarouselItem,
  MenuCarouselProps,
} from 'src/components/organisms/productRouting/menuCarousel'
import {
  Maybe,
  PrismicLinkType,
  PrismicPageDataBodyMenuCarousel,
} from 'src/typings/generated/graphql'

/**
 * The maximum number of characters allowed for the description text in the menu carousel items.
 *
 * If a description exceeds this length, it will be truncated.
 */
const DESCRIPTION_MAX_LENGTH_CHARS = 121

/**
 * Props for the `MenuCarouselSlice` component.
 */
type Props = {
  slice: PrismicPageDataBodyMenuCarousel
}

/**
 * A slice component that renders a menu carousel based on the provided Prismic slice data.
 */
const MenuCarouselSlice = ({ slice }: Props) => {
  /**
   * Extracts a description from the provided text, truncating it to a maximum length defined by
   * `DESCRIPTION_MAX_LENGTH_CHARS`.
   */
  const extractDescription = (
    description?: Maybe<string>
  ): string | undefined => {
    return description
      ? description.slice(0, DESCRIPTION_MAX_LENGTH_CHARS)
      : undefined
  }

  /**
   * Extracts a link from the provided Prismic link object if it exists and has a valid URL. Returns `undefined`
   * otherwise.
   */
  const extractLink = (
    link?: Maybe<PrismicLinkType>
  ): PrismicLinkType | undefined => {
    /**
     * Currently, this function always returns `undefined` avoid rendering the carousel cards as links per Raising
     * Cane's request.
     *
     * Replace the return statement with the commented-out line below to restore link functionality in the future.
     */

    // return link?.url ? link : undefined
    return link?.url ? undefined : undefined
  }

  const items: MenuCarouselItem[] = slice.items.map(item => {
    return {
      name: item.name ?? undefined,
      image: item.image?.gatsbyImageData ?? undefined,
      imageAlt: item.image?.alt ?? undefined,
      description: extractDescription(item.description_text),
      calories: item.calories_text ?? undefined,
      link: extractLink(item.link),
      ctaText: item.link_text ?? undefined,
    }
  })

  const props: MenuCarouselProps = {
    anchor: slice.primary.anchor ?? undefined,
    items,
  }

  return <MenuCarousel {...props} />
}

export default MenuCarouselSlice

export const MenuCarouselFragment = graphql`
  fragment PageDataBodyMenuCarousel on PrismicPageDataBodyMenuCarousel {
    id
    primary {
      anchor
    }
    items {
      name
      image {
        gatsbyImageData
        alt
      }
      description_text
      calories_text
      link {
        ...BasicLinkFragment
      }
      link_text
    }
  }
`
